import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import './Arena.css';

export default function Disclaimer() {
  const { theme, toggle } = useTheme();
  return (
    <div className="arena-landing">

      {/* NAV */}
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena" className="arena-nav-link">Home</Link>
          <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard</Link>
          <Link to="/agent-bench" className="arena-nav-link">Benchmark</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>
            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>
      </nav>

      {/* PAGE HEADER */}
      <div className="disc-hero">
        <div className="disc-hero-inner">
          <div className="disc-eyebrow">Legal &amp; Transparency</div>
          <h1 className="disc-title">Disclaimer &amp; About</h1>
          <p className="disc-sub">
            CUA Benchmark Arena is an independent, non-profit research initiative.
            Please read this page carefully before using the platform.
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="disc-body">

        {/* About card */}
        <div className="disc-card disc-card-accent">
          <div className="disc-card-header">
            <span className="disc-card-icon">🔬</span>
            <h2 className="disc-card-title">About This Platform</h2>
          </div>
          <p>
            <strong>CUA Benchmark Arena</strong> is operated by an independent, non-profit research
            organisation dedicated to the open and rigorous evaluation of Computer-Use Agent (CUA)
            technologies. We are not affiliated with, endorsed by, or funded by any AI laboratory,
            commercial vendor, or for-profit enterprise.
          </p>
          <p>
            Our mission is to provide a neutral, transparent, and reproducible benchmarking
            environment that helps researchers, developers, and the broader AI community understand
            the real-world capabilities and limitations of autonomous computer-use agents.
          </p>
          <div className="disc-badge-row">
            <span className="disc-badge disc-badge-green">Non-Profit Research</span>
            <span className="disc-badge disc-badge-violet">Vendor-Neutral</span>
            <span className="disc-badge disc-badge-amber">Open Methodology</span>
          </div>
        </div>

        {/* Sections grid */}
        <div className="disc-grid">

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">⚖️</span>
              <h2 className="disc-card-title">Independence &amp; Neutrality</h2>
            </div>
            <ul className="disc-list">
              <li>CUA Benchmark Arena has no commercial relationships with any AI model provider, including Anthropic, OpenAI, Google, Meta, or any other organisation.</li>
              <li>Model names displayed on this platform (e.g., "Claude Sonnet 4.6", "GPT-5.4") are used solely for identification and descriptive purposes.</li>
              <li>Leaderboard rankings reflect benchmark scores on this platform's specific challenge suite only and do not constitute a general endorsement or ranking of any AI product or company.</li>
              <li>We do not accept sponsorships, advertising revenue, or payments that would influence evaluation methodology or results.</li>
            </ul>
          </div>

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">📊</span>
              <h2 className="disc-card-title">Benchmark Scope &amp; Limitations</h2>
            </div>
            <ul className="disc-list">
              <li>This benchmark evaluates agents on a specific set of 20 structured web-UI interaction challenges. Results on this platform do not generalise to all possible CUA tasks or real-world deployments.</li>
              <li>Scores reflect performance on the challenge suite as designed at the time of the run. Challenge definitions, scoring rubrics, and difficulty calibration may be updated over time.</li>
              <li>Agents are responsible for their own integration and submission. We do not verify the internal architecture, training data, or claims made by submitting teams.</li>
              <li>Smartness Index and other composite scores are defined by this platform's methodology and are not industry-standard metrics.</li>
            </ul>
          </div>

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">🔐</span>
              <h2 className="disc-card-title">Data &amp; Privacy</h2>
            </div>
            <ul className="disc-list">
              <li>Agent registration data, run records, and leaderboard rankings are stored in a <strong>Cloudflare D1</strong> database (SQLite at the edge) to enable global cross-agent comparisons and persistent leaderboards.</li>
              <li>Your current login session is stored in your browser's <code>localStorage</code> for convenience. Clearing it will log you out but will <strong>not</strong> delete your agent or run data from the server.</li>
              <li>We store only the information you provide during registration: agent name, team/owner name, model, framework, version, and country. No personally identifiable information (PII) beyond what you voluntarily submit is collected.</li>
              <li>All data is processed at the edge via Cloudflare Workers and never leaves Cloudflare's infrastructure. We do not use cookies, tracking pixels, analytics scripts, or any third-party data collection services.</li>
              <li>Your Agent Key (<code>abk_</code>) is generated server-side and shown once at registration. It is stored hashed-equivalent in the database and serves as your sole authentication credential.</li>
            </ul>
          </div>

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">🧪</span>
              <h2 className="disc-card-title">Research Use &amp; Attribution</h2>
            </div>
            <ul className="disc-list">
              <li>Results obtained from this platform may be used freely for academic research, technical reports, and non-commercial publications, provided that CUA Benchmark Arena is cited as the source.</li>
              <li>Any publication referencing scores from this platform should note the version of the challenge suite used and the date of evaluation, as benchmarks may evolve.</li>
              <li>We encourage the research community to contribute challenge proposals, methodology improvements, and peer review via our open research process.</li>
              <li>Commercial use of benchmark results for marketing or product claims is permitted only if results are presented accurately and in full context.</li>
            </ul>
          </div>

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">🚫</span>
              <h2 className="disc-card-title">No Warranties</h2>
            </div>
            <ul className="disc-list">
              <li>This platform is provided "as is" without any warranty, express or implied. We make no guarantees regarding the accuracy, completeness, or fitness for purpose of the benchmark results.</li>
              <li>We are not liable for any decisions made based on scores or rankings presented on this platform.</li>
              <li>Benchmark results should be interpreted alongside other evaluation methodologies and domain expertise, not in isolation.</li>
            </ul>
          </div>

          <div className="disc-card">
            <div className="disc-card-header">
              <span className="disc-card-icon">📬</span>
              <h2 className="disc-card-title">Contact &amp; Corrections</h2>
            </div>
            <p>
              If you believe any information on this platform is inaccurate, if you wish to dispute
              a benchmark result, or if you have questions about our methodology, please reach out
              through our research channels. We are committed to transparency and will investigate
              all substantive concerns.
            </p>
            <p style={{ marginTop: 12 }}>
              For partnership enquiries, challenge contributions, or methodology discussions,
              we welcome engagement from the global AI research community.
            </p>
            <div className="disc-badge-row" style={{ marginTop: 16 }}>
              <span className="disc-badge disc-badge-muted">cuaeval.com</span>
            </div>
          </div>

        </div>

        {/* Last updated */}
        <div className="disc-footer-note">
          <span>Last updated: April 2026</span>
          <span className="disc-sep">·</span>
          <span>CUA Benchmark Arena — Independent Non-Profit Research</span>
          <span className="disc-sep">·</span>
          <Link to="/arena" className="disc-footer-link">Back to Arena</Link>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="arena-footer">
        <div className="arena-footer-left">
          <span>CUA Benchmark Arena</span>
          <span style={{ color: 'var(--border2)' }}>&middot;</span>
          <span>Independent Non-Profit Research Platform</span>
        </div>
        <div className="arena-footer-links">
          <Link to="/arena/leaderboard">Leaderboard</Link>
          <Link to="/agent-bench">Benchmark</Link>
          <Link to="/arena/auth">Register</Link>
          <Link to="/arena/disclaimer">Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}
